import { z } from 'zod';

import { TypeLandingPage } from '../models/contentful-generated';
import { ContentfulSysSchema } from './contentful-sys.validator';
import { filterUnresolvedLink } from './preprocessors';
import { SeoMetadataEntrySchema } from './seo-metadata.validator';

const LandingPageFieldsSchema: z.ZodType<any> = z.lazy(() =>
  z.looseObject({
    internalName: z.string(),
    parentPage: LandingPageEntrySchema.optional().nullable(),
    route: z.string().optional().nullable(),
    slug: z.string().optional().nullable(),
    routeIds: z.array(z.string()).optional().nullable(),
    accessType: z.enum(['Authenticated Only', 'CSR Only', 'Public']),
    theme: z.string(),
    seoMetadata: z.preprocess(filterUnresolvedLink, SeoMetadataEntrySchema.optional().nullable()).optional().nullable(),
    pageContent: z.array(z.any()).optional().nullable(),
    translations: z.record(z.string(), z.any()).optional().nullable()
  })
);

export const LandingPageEntrySchema = z.object({
  sys: ContentfulSysSchema,
  fields: LandingPageFieldsSchema,
  metadata: z
    .object({
      tags: z.array(z.any()).optional().nullable()
    })
    .optional()
    .nullable()
});

export function validateLandingPageEntry(data: TypeLandingPage) {
  return LandingPageEntrySchema.safeParse(data);
}

/**
 * Returns validation result.
 *
 * @param data
 */
export function validateLandingPageEntries(data: TypeLandingPage[]) {
  return z.array(LandingPageEntrySchema).safeParse(data);
}

/**
 * Returns valid entries only. In most cases, this is preferred,
 * It allows us to still show some entries instead of showing nothing when only a single entry is invalid.
 *
 * @param data
 */
export function validateAndFilterLandingPageEntries(data: TypeLandingPage[]) {
  if (!data) return [];

  return data.reduce((acc: TypeLandingPage[], entry) => {
    const validationResult = validateLandingPageEntry(entry);
    if (validationResult.success) {
      acc.push(validationResult.data as unknown as TypeLandingPage);
    } else {
      console.warn(
        `Validation failed for landing page entry with id: '${entry?.sys?.id}'. It will be filtered out. Error: ${validationResult.error.message}`
      );
    }
    return acc;
  }, []);
}

/**
 * Slim schema for landing page entries fetched via the page-picker query.
 * Only validates the fields that are explicitly selected in that query
 * (sys, internalName, route, accessType, theme) — all other fields are absent from the response.
 */
const LandingPagePickerFieldsSchema = z.looseObject({
  internalName: z.string(),
  route: z.string().optional().nullable(),
  accessType: z.enum(['Authenticated Only', 'CSR Only', 'Public']),
  theme: z.string()
});

const LandingPagePickerEntrySchema = z.object({
  sys: ContentfulSysSchema,
  fields: LandingPagePickerFieldsSchema,
  metadata: z
    .object({
      tags: z.array(z.any()).optional().nullable()
    })
    .optional()
    .nullable()
});

/**
 * Returns valid picker-summary entries only, filtering out any entries whose
 * required subset of fields is missing or malformed.
 */
export function validateAndFilterLandingPagePickerEntries(data: TypeLandingPage[]) {
  if (!data) return [];

  return data.reduce((acc: TypeLandingPage[], entry) => {
    const validationResult = LandingPagePickerEntrySchema.safeParse(entry);
    if (validationResult.success) {
      acc.push(validationResult.data as unknown as TypeLandingPage);
    } else {
      console.warn(
        `Validation failed for landing page picker entry with id: '${entry?.sys?.id}'. It will be filtered out. Error: ${validationResult.error.message}`
      );
    }
    return acc;
  }, []);
}

